import { useState } from 'react';
import { X, Mail, MessageCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MessagingModal({ 
  isOpen, 
  onClose, 
  toResort, 
  fromResort,
  messageType = 'email' // 'email' or 'whatsapp'
}) {
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (messageType === 'email') {
        const response = await fetch(`${BACKEND_URL}/api/messages/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            from_resort_id: fromResort.id,
            from_resort_name: fromResort.name,
            from_resort_email: fromResort.email,
            from_resort_phone: fromResort.phone,
            to_resort_id: toResort.id,
            to_resort_name: toResort.name,
            to_resort_email: toResort.email,
            subject: formData.subject,
            message: formData.message,
            subscription_plan: fromResort.subscriptionPlan
          })
        });

        const data = await response.json();
        
        if (data.success) {
          setSuccess(true);
          setQuota(data.quota);
          setTimeout(() => {
            onClose();
            setSuccess(false);
            setFormData({ subject: '', message: '' });
          }, 3000);
        } else {
          setError(data.error || 'Failed to send email');
        }
      } else {
        // For WhatsApp, just track and open WhatsApp
        const response = await fetch(
          `${BACKEND_URL}/api/messages/track-whatsapp?resort_id=${fromResort.id}&subscription_plan=${fromResort.subscriptionPlan}`,
          { method: 'POST' }
        );
        
        const data = await response.json();
        
        if (data.success) {
          // Open WhatsApp with pre-filled message
          const message = `Hi ${toResort.name}, I'm ${fromResort.name} from Travelodges network. I'd like to connect regarding potential collaboration opportunities.`;
          const whatsappUrl = `https://wa.me/${toResort.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
          
          setSuccess(true);
          setQuota(data.quota);
          setTimeout(() => {
            onClose();
            setSuccess(false);
          }, 2000);
        } else {
          setError(data.error || 'Message quota exceeded');
        }
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            {messageType === 'email' ? (
              <Mail className="h-6 w-6 text-emerald-600" />
            ) : (
              <MessageCircle className="h-6 w-6 text-green-600" />
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {messageType === 'email' ? 'Send Email' : 'Connect via WhatsApp'}
              </h2>
              <p className="text-sm text-gray-600">To: {toResort.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="m-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-emerald-800 font-medium">
              ✅ {messageType === 'email' ? 'Email sent successfully!' : 'WhatsApp opened successfully!'}
            </p>
            {quota && (
              <p className="text-sm text-emerald-700 mt-1">
                Messages remaining this month: {quota.remaining === 'Unlimited' ? 'Unlimited' : quota.remaining}
              </p>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">❌ {error}</p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6">
            {messageType === 'email' ? (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Enter subject..."
                    required
                    className="w-full"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Enter your message..."
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
              </>
            ) : (
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Clicking "Open WhatsApp" will open a chat with {toResort.name} with a pre-filled message.
                  You can edit the message before sending.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <strong>Phone:</strong> {toResort.phone}
                  </p>
                </div>
              </div>
            )}

            {/* Subscription Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Your Plan:</strong> {fromResort.subscriptionPlan}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {fromResort.subscriptionPlan === 'Basic' && 'You can send 5 messages per month'}
                {fromResort.subscriptionPlan === 'Pro' && 'You can send 25 messages per month'}
                {fromResort.subscriptionPlan === 'Premium' && 'Unlimited messages'}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {messageType === 'email' ? 'Send Email' : 'Open WhatsApp'}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default MessagingModal;
